import React, { useEffect, useState } from "react";
import { PlayCircle, BookOpen, GraduationCap } from "lucide-react";
import AboutBannerImage1 from "../../src/assets/images/dna1.png";
import AboutBannerImage2 from "../../src/assets/images/aboutbanner2.jpeg";
import AboutBannerImage3 from "../../src/assets/images/dna1.png";
import AboutBannerImage4 from "../../src/assets/images/aboutbanner2.jpeg";
import { BarChart3, CheckSquare, Target, Star } from "lucide-react";
import DashboardImage from "../../src/assets/images/Learning Progress.jpg.jpeg";
import { Database, ShieldCheck, PieChart, Smartphone } from "lucide-react";
import { Activity, Heart, Briefcase, Smile } from "lucide-react";
import { getTestimonial, getInstructors } from "../api/api";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";

const audience = [
    {
        id: 1,
        title: "Fitness Trainers",
        icon: <Activity className="w-4 h-4 text-gray-500" />,
    },
    {
        id: 2,
        title: "Heathcare Professionals",
        icon: <BookOpen className="w-4 h-4 text-gray-500" />,
    },
    {
        id: 3,
        title: "Clinical Aspirants",
        icon: <Heart className="w-4 h-4 text-gray-500" />,
    },
    {
        id: 4,
        title: "Sports Practioners",
        icon: <Briefcase className="w-4 h-4 text-gray-500" />,
    },
];

const platformFeatures = [
    {
        id: 1,
        title: "Scalable CMS",
        description:
            "Robust course management system built for high-volume content delivery.",
        icon: <Database className="w-6 h-6 text-gray-300" />,
    },
    {
        id: 2,
        title: "Secure Enrollment",
        description:
            "Bank-grade secure payment gateways and encrypted user enrollment.",
        icon: <ShieldCheck className="w-6 h-6 text-gray-300" />,
    },
    {
        id: 3,
        title: "Deep Analytics",
        description:
            "Comprehensive user analytics, engagement tracking, and learning reports.",
        icon: <PieChart className="w-6 h-6 text-gray-300" />,
    },
    {
        id: 4,
        title: "Omnichannel Sync",
        description:
            "Seamlessly integrates with the entire Nutrillion ecosystem.",
        icon: <Smartphone className="w-6 h-6 text-gray-300" />,
    },
];

const tools = [
    {
        id: 1,
        title: "Recorded Academic Sessions",
        description:
            "Concepts are developed in a structured academic sequence so learners build deeper subject clarity through programs, helping them move beyond surface-level recommendations toward stronger scientific understanding.",
        icon: <PlayCircle className="text-green-500 w-6 h-6" />,
        border: "border-green-500",
    },
    {
        id: 2,
        title: "Digital Library Access",
        description:
            "After enrollment, learners receive guided access to Nutrillion’s digital learning library, where research papers, academic references, articles, and case-based materials are organized according to each module and topic being studied.",
        icon: <BookOpen className="text-purple-500 w-6 h-6" />,
        border: "border-purple-500",
    },
    {
        id: 3,
        title: "Industry-Recognized Certification",
        description:
            "After completing the program, learners receive certification that strengthens their academic profile in nutrition-related roles and advanced learning pathways within the field of nutrition science.",
        icon: <GraduationCap className="text-green-500 w-6 h-6" />,
        border: "border-green-500",
    },
];

const faculty = [
    {
        id: 1,
        name: "Dr. Sarah Jenkins",
        role: "Clinical Nutritionist",
        description:
            "Ph.D. in Nutritional Sciences with 15+ years of clinical experience.",
        image: AboutBannerImage1,
    },
    {
        id: 2,
        name: "Marcus Thorne, RD",
        role: "Sports Dietitian",
        description:
            "Works with Olympic athletes and specializes in performance nutrition.",
        image: AboutBannerImage2,
    },
    {
        id: 3,
        name: "Priya Patel, MSc",
        role: "Holistic Wellness Coach",
        description:
            "Expert in plant-based diets, gut health, and mindful eating practices.",
        image: AboutBannerImage3,
    },
    {
        id: 4,
        name: "Dr. Robert Vance",
        role: "Metabolic Specialist",
        description:
            "Leading researcher in metabolic disorders and therapeutic diets.",
        image: AboutBannerImage4,
    },
];

const features = [
    {
        id: 1,
        text: "Monitor lesson completion",
        icon: <BarChart3 className="text-green-500 w-5 h-5" />,
    },
    {
        id: 2,
        text: "Review assessment performance ",
        icon: <CheckSquare className="text-green-500 w-5 h-5" />,
    },
    {
        id: 3,
        text: "Seamlessly continue learning without losing progress ",
        icon: <Target className="text-green-500 w-5 h-5" />,
    },
    {
        id: 4,
        text: "Maintain consistency throughout your course journey ",
        icon: <Star className="text-green-500 w-5 h-5" />,
    },
];

const CoursesLearningTools = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [instructors, setInstructors] = useState([]);
    const [instructorsLoading, setInstructorsLoading] = useState(true);
    const [instructorsError, setInstructorsError] = useState(null);

    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                setLoading(true);
                const response = await getTestimonial("course");
                console.log("API Response:", response);

                if (response && response.status === true) {
                    const mappedTestimonials = response.data.map(item => ({
                        id: item.id,
                        text: item.description?.replace(/<[^>]*>/g, ''),
                        name: item.name || item.title,
                        role: item.designation,
                        image: item.imageUrl,
                        description: item.description
                    }));

                    setTestimonials(mappedTestimonials);
                    console.log("Mapped Testimonials:", mappedTestimonials);
                    setError(null);
                } else {
                    console.error("Failed to load testimonials:", response?.message);
                    setTestimonials([]);
                    setError(response?.message || "No testimonials found");
                }
            } catch (error) {
                console.error("Error fetching testimonials:", error);
                setTestimonials([]);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, []);

    useEffect(() => {
        const fetchInstructors = async () => {
            try {
                setInstructorsLoading(true);
                const response = await getInstructors();

                if (response && response.success === true) {
                    const mappedInstructors = response.instructors.map(item => ({
                        id: item.id,
                        name: item.name,
                        role: item.title,
                        bio: item.bio,
                        image: item.profile_image,
                        credentials: item.credentials
                    }));
                    setInstructors(mappedInstructors);
                }
            } catch (error) {
                setInstructorsError(error.message);
            } finally {
                setInstructorsLoading(false);
            }
        };
        fetchInstructors();
    }, []);

    const truncateBio = (bio, maxLength = 120) => {
        if (!bio) return "";
        if (bio.length <= maxLength) return bio;
        return bio.substring(0, maxLength) + "...";
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white py-16 px-6">
                <div className="text-center mb-12">
                    <p className="text-[#6dbe45] text-sm tracking-widest font-semibold mb-2 font-lato">
                        INTERACTIVE LEARNING
                    </p>
                    <h2 className="text-lg sm:text-xl md:text-xl lg:text-4xl font-semibold text-gray-800 font-merriweather">
                       A Learning System Designed for in-depth Understanding 
                    </h2>
                    <p className="text-gray-500 mt-3 font-lato">
Built to help learners develop real-world expertise through structured, immersive, and future-focused education that promotes deep conceptual understanding.                    </p>
                </div>
                <div className="custom-padding xl-screen-padding grid grid-cols-1 md:grid-cols-3 gap-8">
                    {tools.map((tool) => (
                        <div
                            key={tool.id}
                            className={`bg-[#f9f9f9] p-6 rounded-xl shadow-sm border-t-4 ${tool.border}`}
                        >                            <div className="mb-4">
                                {tool.icon}
                            </div>
                            <h3 className="font-semibold text-gray-800 mb-2 font-lato">
                                {tool.title}
                            </h3>
                            <p className="text-sm text-gray-500 leading-relaxed font-lato">
                                {tool.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-[#f7f7f7] py-16 px-6">
                <div className="text-center mb-12">
                    <p className="text-[#6dbe45] text-sm tracking-widest font-semibold mb-2">
                        WORLD-CLASS FACULTY
                    </p>
                    <h2 className="text-lg sm:text-xl md:text-xl lg:text-4xl text-gray-800 font-merriweather">
                        Learn from the Best in Nutrition
                    </h2>
                    <p className="text-gray-500 mt-3 font-lato">
                        Our courses are taught by recognized doctors, certified dietitians, and wellness experts.
                    </p>
                </div>

                {!instructorsLoading && instructors.length > 0 ? (
                    <div className="custom-padding xl-screen-padding grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 text-center">
                        {instructors.map((member) => (
                            <div key={member.id}>
                                <div className="flex justify-center mb-4">
                                    <img
                                        src={member.image || "https://via.placeholder.com/128x128?text=Instructor"}
                                        alt={member.name}
                                        className="w-32 h-32 object-cover rounded-full border-4 border-white shadow-md"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "https://via.placeholder.com/128x128?text=Instructor";
                                        }}
                                    />
                                </div>

                                <h3 className="font-semibold text-gray-800 font-lato">
                                    {member.name}
                                </h3>

                                <p className="text-sm text-gray-500 mb-2 font-lato">
                                    {member.role}
                                </p>

                                <p className="text-sm text-gray-400 font-lato line-clamp-2">
                                    {truncateBio(member.bio)}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    !instructorsLoading && (
                        <div className="text-center py-8">
                        </div>
                    )
                )}

                {instructorsError && (
                    <div className="text-center py-8">
                    </div>
                )}
            </div>

            <div className="bg-white py-16 px-6">
                <div className="custom-padding xl-screen-padding grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="text-[#7c517e] text-sm tracking-widest font-semibold mb-2 font-lato">
                            PERSONALIZED EXPERIENCE
                        </p>

                        <h2 className="text-lg sm:text-xl md:text-xl lg:text-4xl text-gray-800 leading-snug font-merriweather">
Track Your Learning Progress with Ease 
                        </h2>

                        <p className="text-gray-500 mt-4 mb-6 font-lato">
                           Access your personalized learning dashboard to track progress, manage courses and streamline your learning journey in one place. 
                        </p>
                        <p className="text-gray-500 mt-4 mb-6 font-lato">
                            Your personalised dashboard enables you to: </p>

                        <div className="space-y-3">
                            {features.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex items-center gap-3 bg-gray-100 font-semibold px-4 py-3 rounded-lg font-lato"
                                >
                                    {item.icon}
                                    <span className="text-sm text-gray-700">{item.text}</span>
                                </div>
                            ))}
                        </div>
                        <p className="text-gray-500 mt-4 mb-6 font-lato">
                            Everything is intuitively organised, allowing learners to focus entirely on building a strong understanding of the subject. 
                        </p>
                    </div>

                    <div className="relative">
                        <img
                            src={DashboardImage}
                            alt="Dashboard Preview"
                            className="rounded-xl shadow-md w-full object-cover"
                        />
                    </div>
                </div>
            </div>

          {!loading && testimonials.length > 0 && (
            <div className="bg-[#f4f5f7] py-20 px-6">
                <div className="text-center mb-14">
                    <p className="text-green-500 text-sm tracking-widest font-semibold mb-2">
                        SUCCESS STORIES
                    </p>
                    <h2 className="text-lg sm:text-xl md:text-xl lg:text-4xl font-semibold text-gray-800 font-merriweather">
                        Transformations Start Here
                    </h2>
                    <p className="text-gray-500 mt-4 font-lato">
                        Hear from students and professionals who have elevated their careers and
                        lives through Nutrillion Academy.
                    </p>
                </div>

                <div className="relative">
                    <div className="custom-padding xl-screen-padding">
                        <Swiper
                            modules={[Autoplay, Pagination]}
                            spaceBetween={25}
                            slidesPerView={1}
                            pagination={{ 
                                clickable: true,
                                dynamicBullets: false
                            }}
                            autoplay={{
                                delay: 3000,
                                disableOnInteraction: false
                            }}
                            loop={true}
                            breakpoints={{
                                0: {
                                    slidesPerView: 1
                                },
                                768: {
                                    slidesPerView: 2
                                },
                                1024: {
                                    slidesPerView: 3
                                }
                            }}
                            className="testimonialSwiper"
                        >
                            {testimonials.map((item) => (
                                <SwiperSlide key={item.id}>
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 relative h-full">
                                        <div className="flex gap-1 mb-4 text-yellow-400">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} size={16} fill="currentColor" />
                                            ))}
                                        </div>

                                        <div
                                            className="text-gray-600 text-sm leading-relaxed mb-6 font-lato line-clamp-4"
                                            dangerouslySetInnerHTML={{
                                                __html: item.description
                                            }}
                                        />

                                        <div className="flex items-center gap-3">
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="w-10 h-10 rounded-full object-cover"
                                                onError={(e) => {
                                                    e.target.src = "https://via.placeholder.com/40";
                                                }}
                                            />
                                            <div>
                                                <p className="text-sm font-extrabold text-gray-800">
                                                    {item.name}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {item.role}
                                                </p>
                                            </div>
                                        </div>

                                        <span className="absolute top-4 right-6 text-gray-200 text-5xl font-serif">
                                            ”
                                        </span>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
            </div>
          )}

            <div className="bg-white py-20 px-6">
                <div className="text-center mb-12 custom-padding xl-screen-padding">
                    <p className="text-green-500 text-sm tracking-widest font-semibold mb-2">
                        YOUR GROWTH PARTNER
                    </p>

                    <h2 className="text-lg sm:text-xl md:text-xl lg:text-4xl text-gray-800 font-merriweather">
                       For Whom These Programmes Are Designed For?
                    </h2>

                    <p className="text-gray-500 mt-4 mx-auto font-lato">
                       These programmes are tailored for learners seeking to build structured careers in nutrition science and precision nutrition. 
                    </p>
                </div>
                <div className="flex flex-wrap justify-center gap-4 mx-auto">
                    {audience.map((item) => (
                        <div
                            key={item.id}
                            className="flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-lg shadow-sm text-sm font-semibold text-gray-700 hover:shadow-md transition font-lato font-semibold"
                        >
                            {item.icon}
                            {item.title}
                        </div>
                    ))}
                </div>
                <div className="flex justify-center mt-8">
                    <div className="flex items-center gap-2 px-6 py-4 border border-green-400 text-black rounded-xl text-sm font-semibold">
                        <Smile className="w-4 h-4" />
                        Anyone improving their lifestyle
                    </div>
                </div>
            </div>
        </>
    );
};

export default CoursesLearningTools;